import { type NeedsMergePatient, isNeedsMerge } from "../MergeFlow/mergeFlow.props";
import { TransactorKind } from "@vyne-shared/components/Payment/transactorKind";
import { createContext, use, useReducer } from "react";
import type { ExistingInsuranceResult, ExistingPatientResult } from "api/responses";
import type { ExistingPatient } from "../existingPatient";
import type { NiceModalHandler } from "@ebay/nice-modal-react";

export interface SearchFlowIncompleteState {
    merging: false;
    searchComplete: false;
    selectedTransactor: undefined;
}

export interface SearchFlowMergingState {
    merging: true;
    searchComplete: true;
    selectedTransactor: NeedsMergePatient;
}

export interface SelectedPatient {
    kind: TransactorKind.Patient;
    transactor: NonNullable<ExistingPatient>;
}

export interface SelectedInsuranceCarrier {
    kind: TransactorKind.InsuranceCarrier;
    transactor: Omit<ExistingInsuranceResult, "updateDate">;
}

export interface SearchFlowCompleteState {
    merging: false;
    searchComplete: true;
    selectedTransactor: undefined | SelectedPatient | SelectedInsuranceCarrier;
}

export type SearchFlowState = SearchFlowMergingState | SearchFlowIncompleteState | SearchFlowCompleteState;

export type SearchFlowContextProps = SearchFlowState & {
    onSelectNewPrp: () => void;
    onSelectExistingPrp: (prp: ExistingPatientResult) => void;
    onSelectInsuranceCarrier: (name: string) => void;
    onReset: () => void;
    modal: NiceModalHandler;
    isProvisioning: boolean;
};

export const SearchFlowContext = createContext<SearchFlowContextProps | undefined>(undefined);

export interface SearchFlowSelectNewPatientAction {
    type: "select-new-patient";
}

export interface SearchFlowSelectExistingPatientAction {
    type: "select-existing-patient";
    existingPrp: ExistingPatientResult;
}

export interface SearchFlowSelectInsuranceCarrierAction {
    type: "select-insurance-carrier";
    name: string;
}

interface SearchFlowResetAction {
    type: "reset";
}

export type SearchFlowAction =
    | SearchFlowSelectExistingPatientAction
    | SearchFlowSelectNewPatientAction
    | SearchFlowSelectInsuranceCarrierAction
    | SearchFlowResetAction;

const initialSearchFlowState: SearchFlowState = Object.freeze({
    merging: false,
    searchComplete: false,
    selectedTransactor: undefined,
});

export function useSearchFlowReducer(): [SearchFlowState, React.ActionDispatch<[action: SearchFlowAction]>] {
    return useReducer(reducer, initialSearchFlowState);
}

function reducer(state: SearchFlowState, action: SearchFlowAction): SearchFlowState {
    // only allow reset for switching tabs after search is complete
    if (state.searchComplete && !state.merging && action.type !== "reset") {
        console.warn("search flow: no additional actions allowed", action, state);
        return state;
    }

    switch (action.type) {
        case "select-new-patient": {
            return {
                merging: false,
                searchComplete: true,
                selectedTransactor: undefined, // Explicitly selected to create a new patient in a non-pms-integrated scenario
            };
        }
        case "select-existing-patient": {
            const { existingPrp } = action;
            return isNeedsMerge(existingPrp)
                ? {
                      merging: true,
                      searchComplete: true,
                      selectedTransactor: existingPrp, // NeedsMergePatient
                  }
                : {
                      merging: false,
                      searchComplete: true,
                      selectedTransactor: { kind: TransactorKind.Patient, transactor: existingPrp },
                  };
        }
        case "select-insurance-carrier": {
            const { name } = action;
            return {
                merging: false,
                searchComplete: true,
                selectedTransactor: { kind: TransactorKind.InsuranceCarrier, transactor: { name, id: 0 } },
            };
        }
        case "reset": {
            return initialSearchFlowState;
        }
        default:
            throw new Error(`Unknown action: ${JSON.stringify(action)}`);
    }
}

export function useSearchFlowContext() {
    const flow = use(SearchFlowContext);
    if (!flow) throw new Error("Payment context is required");
    return flow;
}
